import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, output, signal } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { PropertyService } from "../../../../services/property.service";
import { CityList, CountyList, StateList } from "../../../../models/RequestQuote";
import { forkJoin, map, Subscription } from "rxjs";
import { ConfirmationDialogComponent } from "../../../utils/confirmation-dialog/confirmation-dialog.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { BranchDetailsResponse, CountriesList } from "../../../../models/SystemAdmin.model";
import { companyLandingGrids } from "../../../../models/SystemAdmin.model";  
import { ToastService } from "../../../../services/toast.service";
import { CompanyType, CountryType, ToastType } from "../../../../enums/config.enum";
import { MatSelectModule } from "@angular/material/select";
import { SpinnerComponent } from "../../../utils/spinner/spinner.component";
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

@Component({
    selector: 'app-branch-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatDialogModule, ReactiveFormsModule, MatSelectModule, SpinnerComponent, NgxIntlTelInputModule],
    templateUrl: './branch-details.component.html',
    styleUrl: './branch-details.component.scss'
})
export class BranchDetails implements OnInit, OnDestroy{
    @Input() branchDetailsForm!: FormGroup;
    @Input() branchId!: string;
    @Input() isCreateBranch!: boolean;
    @Output() isAdminCompany = new EventEmitter<boolean>();
    @Output() countryChange = new EventEmitter<number>();
    // @Input() formatPhoneNumber : (phoneNumber: string | null) => string | null = (phoneNumber: string | null) => null;
    @Input() extractRawPhoneNumber : (phoneNumber: string | null) => string | null = (phoneNumber: string | null) => null;
    @Output() countryIdChange = new EventEmitter<string | null>();
    isLoading = signal<boolean>(false);
    countryId: number | null = null;
    rawPhoneNumber: string | null = null;
    companyList = signal<companyLandingGrids[]>([]);
    statesList = signal<StateList[]>([]);
    countyList = signal<CountyList[]>([]);
    citiesList = signal<CityList[]>([]);
    countryList = signal<CountriesList[]>([]);
    searchState = signal<any>('');
    searchCounty = signal<any>('');
    searchCity = signal<any>('');
    filteredStatesList = signal<StateList[]>([]);
    filteredCountyList = signal<CountyList[]>([]);
    filteredCityList = signal<CityList[]>([]);
    branchDetailsSubmitEmitter = output<boolean>();
    router = inject(Router);
    activateRoute = inject(ActivatedRoute);
    dialog = inject(MatDialog);
    propertyService = inject(PropertyService);
    systemAdminService = inject(SystemAdminService);
    toastService = inject(ToastService);
    companySubscription: Subscription = new Subscription();
    stateSubscription: Subscription = new Subscription();
    countySubscription: Subscription = new Subscription();
    citySubscription: Subscription = new Subscription();
    countrySubscription = new Subscription();
    countrySelectedValue = signal('');
    phoneInputSubscription: Subscription = new Subscription();
    CountryISO = CountryISO;
    SearchCountryField = SearchCountryField;
    phoneUtil = PhoneNumberUtil.getInstance();
    
    // Get all countries from PhoneNumberUtil for dropdown
    getAllCountries(): { code: CountryISO; name: string; displayName: string }[] {
      const countries: { code: CountryISO; name: string; displayName: string }[] = [];
      try {
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const countryCode = region as CountryISO;
            const countryCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            const countryName = this.getCountryName(countryCode);
            const displayName = countryCallingCode ? `+${countryCallingCode} ${countryName}` : countryName;
            countries.push({ code: countryCode, name: countryName, displayName });
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        countries.push(
          { code: CountryISO.UnitedStates, name: 'United States', displayName: '+1 United States' },
          { code: CountryISO.UnitedKingdom, name: 'United Kingdom', displayName: '+44 United Kingdom' }
        );
      }
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    getCountryName(countryCode: CountryISO): string {
      const codeStr = String(countryCode).toUpperCase();
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(codeStr);
        if (countryName) {
          return countryName;
        }
      } catch (error) {
        // Fallback to hardcoded mapping
      }
      const countryNames: { [key: string]: string } = {
        'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
        'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
        'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
        'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland',
        'PT': 'Portugal', 'GR': 'Greece', 'LU': 'Luxembourg', 'IS': 'Iceland', 'MT': 'Malta',
        'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania', 'SI': 'Slovenia',
        'SK': 'Slovakia', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia'
      };
      if (countryNames[codeStr]) {
        return countryNames[codeStr];
      }
      return codeStr.split('').map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase()).join('');
    }

    getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 15;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-max-length mapping
      const countryMaxLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 11, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 11, // United Kingdom
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 11, // Germany
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
    }
    
    onBranchPhoneCountryChange(country: any) {
      if (country && country.iso2) {
        this.branchDetailsForm.get('branchPhoneCountryCode')?.setValue(country.iso2, { emitEvent: true });
        // Reset phone number field when country changes
        this.branchDetailsForm.get('branchPhone')?.setValue('', { emitEvent: false });
      }
    }
    
    // Get country calling code from CountryISO (e.g., CountryISO.Afghanistan -> "+93")
    getCountryCallingCode(countryCode: CountryISO | null | undefined): string | null {
      if (!countryCode) {
        return null;
      }
      try {
        const callingCode = this.phoneUtil.getCountryCodeForRegion(countryCode);
        return callingCode ? `+${callingCode}` : null;
      } catch {
        return null;
      }
    }
    
    // Convert calling code string (e.g., "+93") to CountryISO
    mapCallingCodeToISO(callingCode: string | number | null): CountryISO | null {
      if (!callingCode) return null;
      
      const codeStr = String(callingCode).replace(/^\+/, ''); // Remove + if present
      const codeNum = parseInt(codeStr, 10);
      
      if (isNaN(codeNum)) return null;
      
      try {
        // Iterate through all supported regions to find matching calling code
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            if (regionCallingCode === codeNum) {
              // Convert to uppercase to match CountryISO enum values
              return region.toUpperCase() as CountryISO;
            }
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        // Fallback to common mappings
      }
      
      // Fallback mappings for common countries
      if (codeNum === 1) return CountryISO.UnitedStates;
      if (codeNum === 44) return CountryISO.UnitedKingdom;
      if (codeNum === 93) return CountryISO.Afghanistan;
      
      return null;
    }


    ngOnInit(): void {
        this.getCompanyList();
        this.getCountryList();
        if(this.branchId){
            this.fetchBranchDetails(this.branchId);
        }

        // Manually check for null value after the form is initialized
        // this.companySubscription = this.branchDetailsForm.get('branchCompany')?.valueChanges.subscribe(value => {
        //     if (value === "null") {
        //         this.branchDetailsForm.get('branchCompany')?.setErrors({ required: true });
        //         this.branchDetailsForm.get('stateId')?.setValue(null);
        //     } else {
        //         // Clear error if a valid option is selected
        //         this.branchDetailsForm.get('branchCompany')?.setErrors(null);
        //         this.branchDetailsForm.get('stateId')?.setValue(null);
        //     }
        // }) || new Subscription();

        this.stateSubscription = this.branchDetailsForm.get('stateId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchDetailsForm.get('stateId')?.setErrors({ required: true });
            }else{
                // Clear error if a valid option is selected
                this.branchDetailsForm.get('stateId')?.setErrors(null);
                if(value){
                    this.getCountyAndCitiesList(Number(value));
                }
            }
            // this.branchDetailsForm.patchValue({countyId: null, cityId: null});
        }) || new Subscription();

        this.countySubscription = this.branchDetailsForm.get('countyId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchDetailsForm.get('countyId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.branchDetailsForm.get('countyId')?.setErrors(null);
            }
        }) || new Subscription();

        this.citySubscription = this.branchDetailsForm.get('cityId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchDetailsForm.get('cityId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.branchDetailsForm.get('cityId')?.setErrors(null);
            }
        }) || new Subscription();

        this.countrySubscription = this.branchDetailsForm.get('countryId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.branchDetailsForm.get('countryId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.branchDetailsForm.get('countryId')?.setErrors(null);
                if(value && String(value) !== String(this.countrySelectedValue())){
                   this.countrySelectedValue.set(value);
                   this.getStatesByCountryId(Number(value)) // based on the country list selection pass id
                  //  this.countryId = Number(value);
                   
                   // Update countryCode when countryId changes
                  //  if (this.countryList().length > 0) {
                  //    const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(value));
                  //    const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                  //    this.branchDetailsForm.get('countryCode')?.setValue(countryCode);
                     
                  //    // Update branch phone country code
                  //    const branchPhoneCountryCodeISO = countryCode ? this.mapCallingCodeToISO(countryCode) : null;
                  //    if (branchPhoneCountryCodeISO) {
                  //      this.branchDetailsForm.get('branchPhoneCountryCode')?.setValue(branchPhoneCountryCodeISO);
                  //    }
                  //  }  
                }
            }
        }) || new Subscription();
    }

     fetchBranchDetails(branchId: string) {
        this.systemAdminService.getBranchDetails(branchId).subscribe({
          next: (response: BranchDetailsResponse) => {
            if(response){
              this.onBindFormData(response);
              this.systemAdminService.setBranchCountryID(String(response?.branchAddress.countryId))
            }
          },
          error: (error: any) => {
            console.log("error", error);
            this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
        });
    };


    // Progressive live formatter for branchPhone based on current countryId
    formatPhoneNumberLive(inputValue: any): string {
        const raw = (inputValue ?? '').toString();
        // Remove country code prefix if present
        let digits = raw.replace(/\D/g, '');
        const countryId = this.countryId;
        
        // Get country code prefix
        const countryCodePrefix = this.branchDetailsForm.get('countryCode')?.value ?? null;
        const countryCode = countryCodePrefix ? String(countryCodePrefix) : '';
        
        // Remove country code prefix if already present
        if (countryCode) {
          const prefixDigits = countryCode.replace(/\D/g, '');
          if (digits.startsWith(prefixDigits)) {
            digits = digits.substring(prefixDigits.length);
          }
        }
        
        if (!digits) return countryCode ? countryCode + ' ' : '';
        
        if (countryId == 1) {
            const a = digits.slice(0, 3);
            const b = digits.slice(3, 6);
            const c = digits.slice(6, 10);
            const formatted = digits.length <= 3 ? `(${a}` : digits.length <= 6 ? `(${a}) ${b}` : `(${a}) ${b}-${c}`;
            return countryCode ? `${countryCode} ${formatted}` : formatted;
        } else if (countryId == 2) {
            const a = digits.slice(0, 3);
            const b = digits.slice(3, 7);
            const c = digits.slice(7, 11);
            const formatted = digits.length <= 3 ? a : digits.length <= 7 ? `${a} ${b}` : `${a} ${b} ${c}`;
            return countryCode ? `${countryCode} ${formatted}` : formatted;
        }
        return countryCode ? `${countryCode} ${digits}` : digits;
    }

    onBindFormData(formValue: any) {        
        this.branchDetailsForm.patchValue({
          branchName: formValue.branchName,
          branchCode: formValue.branchCode,
          branchCompany: formValue.companyId,
          branchEmail: formValue.branchEmail,
          isActive: formValue.isActive,
          isDeleted: formValue.isDeleted,
          branchAddressId: formValue.branchAddressId,
          unitNum: formValue.branchAddress?.unitNum,
          streetNum: formValue.branchAddress?.streetNum,
          streetName: formValue.branchAddress?.streetName,
          stateId: formValue.branchAddress?.stateId,
          countyId: formValue.branchAddress?.countyId,
          cityId: formValue.branchAddress?.cityId,
          zipCode: formValue.branchAddress?.zipCode,
        });
        
        // Extract phone number - handle both string and number types
        let branchPhone: string | null = null;
        if (formValue?.branchPhone !== null && formValue?.branchPhone !== undefined) {
          branchPhone = String(formValue.branchPhone);
        } else if (formValue?.iPhone) {
          branchPhone = String(formValue.iPhone).replace(/^\+\d+/, '');
        } else if (formValue?.branchIPhone) {
          branchPhone = String(formValue.branchIPhone).replace(/^\+\d+/, '');
        }
        
        const countryId = formValue.branchAddress?.countryId ?? null;
        
        // Get country codes from response - check all three fields and use whichever has a value
        // Priority: BranchCountryCode (capital) > branchCountryCode (lowercase) > countryCode > contactCountryCode
        const BranchCountryCodeFromResponse = formValue?.BranchCountryCode ?? formValue?.branchCountryCode ?? null;
        const countryCodeFromResponse = formValue?.countryCode ?? null;
        const contactCountryCodeFromResponse = formValue?.contactCountryCode ?? null;
        
        // Get ISO2 code from API response (prioritize this over calling codes)
        // Check both uppercase and lowercase property names for compatibility
        const branchPhoneCodeFromResponse = formValue?.BranchPhoneCode ?? formValue?.branchPhoneCode ?? null;
        
        // Check if phone codes are missing (for existing records)
        const phoneCodesMissing = !branchPhoneCodeFromResponse && !BranchCountryCodeFromResponse && 
                                  !countryCodeFromResponse && !contactCountryCodeFromResponse;
        
        // Convert ISO2 code to CountryISO enum (if provided)
        // IMPORTANT: Use ISO2 codes directly from API response to avoid ambiguity
        let branchPhoneCodeISO: CountryISO | null = null;
        
        if (branchPhoneCodeFromResponse) {
          const iso2Upper = String(branchPhoneCodeFromResponse).toUpperCase().trim();
          // Directly use the ISO2 code as CountryISO (CountryISO enum values are ISO2 codes)
          // This ensures we use the exact ISO2 code from API response (e.g., "AI" for Anguilla)
          if (iso2Upper && iso2Upper.length === 2) {
            branchPhoneCodeISO = iso2Upper as CountryISO;
          }
        }
        
        // If phone codes are missing and we have countryId, use it to determine phone country code
        if (phoneCodesMissing && countryId) {
          const countryCodeFromCountryId = this.getCountryCodeFromCountryId(countryId);
          if (countryCodeFromCountryId) {
            // Use countryId to set phone code
            branchPhoneCodeISO = countryCodeFromCountryId;
          }
        }
        
        // Use the first available country code (priority: BranchCountryCode > countryCode > contactCountryCode)
        const branchPhoneCountryCodeValue = BranchCountryCodeFromResponse || countryCodeFromResponse || contactCountryCodeFromResponse;
        
        this.countryId = countryId;
        this.branchDetailsForm.get('countryId')?.setValue(countryId);
        this.countryIdChange.emit(countryId);
        
        // Use ISO2 code directly if available, otherwise fall back to calling code conversion
        // CRITICAL: Always prioritize ISO2 codes from API response to avoid ambiguity
        const branchPhoneCountryCode = branchPhoneCodeISO ?? 
          (branchPhoneCountryCodeValue ? this.mapCallingCodeToISO(String(branchPhoneCountryCodeValue)) : null) ??
          this.getCountryCodeFromCountryId(countryId) ?? 
          CountryISO.UnitedStates;
        
        // Set phone country code FIRST (before formatting phone numbers)
        // CRITICAL: If ISO2 code is available from API response, use it directly
        if (branchPhoneCodeISO) {
          // Use ISO2 code directly from API response (e.g., "AI" for Anguilla)
          this.branchDetailsForm.get('branchPhoneCountryCode')?.setValue(branchPhoneCodeISO, { emitEvent: false });
        } else {
          // Fallback to calling code conversion only if ISO2 is not available
          this.branchDetailsForm.get('branchPhoneCountryCode')?.setValue(branchPhoneCountryCode, { emitEvent: false });
        }
        
        // Ensure the ISO2 code is set after a brief delay to allow ngx-intl-tel-input to initialize
        if (branchPhoneCodeISO) {
          setTimeout(() => {
            this.branchDetailsForm.get('branchPhoneCountryCode')?.setValue(branchPhoneCodeISO, { emitEvent: false });
          }, 100);
        }
        
        // Set countryCode in form - use the first available value
        if (BranchCountryCodeFromResponse) {
          this.branchDetailsForm.get('countryCode')?.setValue(String(BranchCountryCodeFromResponse));
        } else if (countryCodeFromResponse) {
          this.branchDetailsForm.get('countryCode')?.setValue(String(countryCodeFromResponse));
        } else if (contactCountryCodeFromResponse) {
          this.branchDetailsForm.get('countryCode')?.setValue(String(contactCountryCodeFromResponse));
        } else if (countryId !== null && this.countryList().length > 0) {
          const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
          const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
          this.branchDetailsForm.get('countryCode')?.setValue(countryCode);
        }
        
        // Format phone number AFTER country code is set
        setTimeout(() => {
          if (branchPhone !== null && branchPhone !== '') {
            // Double-check that country code is set in the form
            const currentCountryCode = this.branchDetailsForm.get('branchPhoneCountryCode')?.value;
            const countryCodeToUse = branchPhoneCountryCode || currentCountryCode;
            
            const formattedPhone = this.formatPhoneNumberForBinding(branchPhone, countryCodeToUse);
            
            // Ensure we set a value even if formatting returns null (fallback to original)
            if (formattedPhone !== null && formattedPhone !== undefined && formattedPhone !== '') {
              // Use patchValue instead of setValue to avoid triggering valueChanges
              this.branchDetailsForm.patchValue({ branchPhone: formattedPhone }, { emitEvent: false });
              
              // Also update the form control directly to ensure it's set
              const branchPhoneControl = this.branchDetailsForm.get('branchPhone');
              if (branchPhoneControl) {
                branchPhoneControl.setValue(formattedPhone, { emitEvent: false, onlySelf: true });
              }
            } else {
              // If formatting failed, still set the phone number (it will be formatted on next change)
              this.branchDetailsForm.patchValue({ branchPhone: branchPhone }, { emitEvent: false });
            }
          }
        }, 200);
        
        const getSelectedCompanyType = this.companyList().filter((company) => String(company.companyId) === String(formValue.companyId));
        
        if(getSelectedCompanyType.length > 0 && getSelectedCompanyType[0]?.companyType === CompanyType.BROKER_COMPANY){
            // this.branchDetailsForm.get('countryId')?.disable();
            this.isAdminCompany.emit(false);
        }else{
            this.isAdminCompany.emit(true);
        }
    }
    
    private getCountryCodeFromCountryId(countryId: number | null): CountryISO | null {
      if (!countryId) return null;
      if (countryId === 1) return CountryISO.UnitedStates;
      if (countryId === 2) return CountryISO.UnitedKingdom;
      return null;
    }
    
    private formatPhoneNumberForBinding(phoneNumber: string | null, countryCode: CountryISO | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }
      
      // Extract raw digits first
      let raw = this._extractRawPhoneNumber(phoneNumber, countryCode);
      
      // Special handling for UK: if 11 digits, cut the last digit (UK numbers should be 10 digits for mobile or 11 with leading 0)
      if (countryCode === CountryISO.UnitedKingdom && raw && raw.length === 11 && !raw.startsWith('0')) {
        raw = raw.substring(0, 10);
      }
      
      // Format the phone number directly - formatPhoneNumber will handle parsing and formatting
      // It will extract raw digits and format according to the country code
      return this.formatPhoneNumber(raw, countryCode);
    }
    
    private _extractRawPhoneNumber(formattedPhoneNumber: string | null | undefined, countryCode?: CountryISO | null): string | null {
      if (typeof formattedPhoneNumber !== 'string') {
        return null;
      }
      
      // Extract all digits first
      let cleaned = formattedPhoneNumber.replace(/\D/g, '');
      
      if (!cleaned || cleaned.length === 0) {
        return null;
      }
      
      if (!countryCode) {
        return cleaned;
      }

      // Try to parse and extract national number
      try {
        // Convert CountryISO to lowercase string region code for phoneUtil.parse()
        const regionCode = countryCode ? String(countryCode).toLowerCase() : undefined;
        if (!regionCode) {
          return cleaned;
        }
        const parsedNumber = this.phoneUtil.parse(cleaned, regionCode);
        
        // Get national number (without country code)
        const nationalNumber = parsedNumber.getNationalNumber();
        if (nationalNumber) {
          return String(nationalNumber);
        }
      } catch (error) {
        // Parsing failed - might be incomplete number, try to remove country code prefix manually
      }
      
      // Fallback: remove country code prefix if present
      const countryCodePrefix = this.getCountryCodePrefix(countryCode);
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        // Check if the cleaned number starts with the country code prefix
        if (cleaned.startsWith(prefixDigits)) {
          // Remove the prefix to get national number
          cleaned = cleaned.substring(prefixDigits.length);
          // For UK (44), also remove leading 0 if present (UK national format starts with 0)
          if (countryCode === CountryISO.UnitedKingdom && cleaned.startsWith('0')) {
            cleaned = cleaned.substring(1);
          }
        }
      }
      
      return cleaned;
    }
    
    private formatPhoneNumber(phoneNumber: string | null, countryCode: CountryISO | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }
      
      if (!countryCode) {
        return phoneNumber;
      }
      
      try {
        // Remove all non-digit characters for parsing
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        
        // Special handling for UK to ensure consistent formatting - ALWAYS use this for UK
        if (countryCode === CountryISO.UnitedKingdom) {
          // UK numbers: 10 digits (mobile) or 11 digits (landline)
          if (cleanedNumber.length <= 4) {
            return cleanedNumber;
          } else if (cleanedNumber.length <= 7) {
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
          } else if (cleanedNumber.length === 10) {
            // 10 digits: XXXX XXXXXX (e.g., 7655 677666)
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
          } else if (cleanedNumber.length === 11) {
            // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
          } else {
            // More than 11 digits: truncate to 11 and format
            const truncated = cleanedNumber.substring(0, 11);
            return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
          }
        }
        
        // Convert CountryISO to lowercase string region code for phoneUtil.parse()
        const regionCode = String(countryCode).toLowerCase();
        
        // Parse the phone number with the selected country code
        const parsedNumber = this.phoneUtil.parse(cleanedNumber, regionCode);
        
        // Format as national number (without country code) since country code is displayed separately
        try {
          const formatted = this.phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
          
          // Remove any leading country code that might be included
          let finalFormatted = formatted.replace(/^\+\d+\s*/, '').trim();
          // Remove leading "0" that NATIONAL format adds for some countries (e.g., Afghanistan, UK)
          // Since country code is displayed separately, we don't need this leading "0"
          finalFormatted = finalFormatted.replace(/^0+/, '').trim();
          return finalFormatted;
        } catch (formatError) {
          const progressiveResult = this.formatPhoneNumberProgressive(cleanedNumber, countryCode);
          return progressiveResult;
        }
      } catch (error) {
        const fallbackResult = this.formatPhoneNumberFallback(phoneNumber, countryCode);
        return fallbackResult;
      }
    }
    
    private formatPhoneNumberProgressive(cleanedNumber: string, countryCode: CountryISO | null): string {
      if (!countryCode || !cleanedNumber) {
        return cleanedNumber;
      }
      
      // Format progressively based on country
      if (countryCode === CountryISO.UnitedStates) {
        // US format: (XXX) XXX-XXXX
        if (cleanedNumber.length <= 3) {
          return cleanedNumber.length > 0 ? `(${cleanedNumber}` : '';
        } else if (cleanedNumber.length <= 6) {
          return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3)}`;
        } else {
          return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6, 10)}`;
        }
      } else if (countryCode === CountryISO.UnitedKingdom) {
        // UK format: XXXX XXX XXXX or variations
        if (cleanedNumber.length <= 4) {
          return cleanedNumber;
        } else if (cleanedNumber.length <= 7) {
          return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
        } else {
          return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
        }
      }
      
      // Default: return as is
      return cleanedNumber;
    }
    
    private formatPhoneNumberGeneric(cleanedNumber: string, countryCode: CountryISO | null): string {
      if (!cleanedNumber) {
        return cleanedNumber;
      }
      
      
      // Apply generic formatting based on number length
      // Most countries use spaces or hyphens to separate number groups
      const length = cleanedNumber.length;
      let result: string;
      
      if (length <= 3) {
        result = cleanedNumber;
      } else if (length <= 6) {
        // Format as XXX XXX
        result = `${cleanedNumber.slice(0, 3)} ${cleanedNumber.slice(3)}`;
      } else if (length <= 9) {
        // Format as XXX XXX XXX
        result = `${cleanedNumber.slice(0, 3)} ${cleanedNumber.slice(3, 6)} ${cleanedNumber.slice(6)}`;
      } else if (length === 10) {
        // Special case for 10-digit numbers: XXX XXX XXXX (like US format but without parentheses)
        result = `${cleanedNumber.slice(0, 3)} ${cleanedNumber.slice(3, 6)} ${cleanedNumber.slice(6)}`;
      } else if (length <= 12) {
        // Format as XXX XXX XXX XXX
        result = `${cleanedNumber.slice(0, 3)} ${cleanedNumber.slice(3, 6)} ${cleanedNumber.slice(6, 9)} ${cleanedNumber.slice(9)}`;
      } else {
        // For very long numbers, format in groups of 3-4 digits
        let formatted = '';
        let index = 0;
        while (index < length) {
          if (formatted) formatted += ' ';
          const groupSize = (length - index) > 4 ? 4 : (length - index);
          formatted += cleanedNumber.slice(index, index + groupSize);
          index += groupSize;
        }
        result = formatted;
      }
      
      return result;
    }
    
    private formatPhoneNumberFallback(phoneNumber: string, countryCode: CountryISO | null | undefined): string {
      if (!countryCode) {
        return phoneNumber;
      }
      
      const cleanedNumber = phoneNumber.replace(/\D/g, '');
      
      // Get country code prefix
      const countryCodePrefix = this.getCountryCodePrefix(countryCode);

      // Remove country code prefix if already present
      let digits = cleanedNumber;
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        if (digits.startsWith(prefixDigits)) {
          digits = digits.substring(prefixDigits.length);
        }
      }

      // Format based on country (without country code prefix since it's displayed separately)
      if (countryCode === CountryISO.UnitedStates && digits.length === 10) {
        const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
      } else if (countryCode === CountryISO.UnitedStates && digits.length > 0) {
        // Progressive formatting for US
        return this.formatPhoneNumberProgressive(digits, countryCode);
      } else if (countryCode === CountryISO.UnitedKingdom && digits.length >= 10) {
        // UK numbers can vary, try common format
        if (digits.length === 11) {
          const match = digits.match(/^(\d{4})(\d{3})(\d{4})$/);
          if (match) {
            return `${match[1]} ${match[2]} ${match[3]}`;
          }
        }
        // Progressive formatting for UK
        return this.formatPhoneNumberProgressive(digits, countryCode);
      }

      return digits;
    }
    
    private getCountryCodePrefix(countryCode: CountryISO | null | undefined): string {
      if (!countryCode) {
        return '';
      }
      try {
        // Convert CountryISO to lowercase string region code for phoneUtil.getCountryCodeForRegion()
        const regionCode = String(countryCode).toLowerCase();
        const callingCode = this.phoneUtil.getCountryCodeForRegion(regionCode);
        return callingCode ? `+${callingCode}` : '';
      } catch {
        return '';
      }
    }

    private formatPhoneNumberWithCountryCode(phoneNumber: string | null, countryCode: string | null, countryId: number | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }

      // Use countryCode from form if available, otherwise try to get from countryList
      let countryCodePrefix = countryCode ? String(countryCode) : '';
      if (!countryCodePrefix && countryId && this.countryList().length > 0) {
        const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
        countryCodePrefix = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : '';
      }

      // Remove country code prefix if already present
      let cleanedNumber = phoneNumber.replace(/\D/g, ''); // Remove non-numeric characters
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        if (prefixDigits && cleanedNumber.startsWith(prefixDigits)) {
          cleanedNumber = cleanedNumber.substring(prefixDigits.length);
        }
      }

      // If the number has fewer digits than required for a valid format, return with prefix if available
      if ((countryId == 1 && cleanedNumber.length < 10) || (countryId == 2 && cleanedNumber.length < 11)) {
        return countryCodePrefix ? `${countryCodePrefix} ${cleanedNumber}` : cleanedNumber;
      }

      // Format for USA (countryId 1) - +1 (XXX) XXX-XXXX
      if (countryId == 1 && cleanedNumber.length === 10) {
        const match = cleanedNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          return countryCodePrefix ? `${countryCodePrefix} (${match[1]}) ${match[2]}-${match[3]}` : `(${match[1]}) ${match[2]}-${match[3]}`;
        }
      } 
      // Format for UK (countryId 2) - +44 XXX XXX XXXX
      else if (countryId == 2 && cleanedNumber.length === 11) {
        const match = cleanedNumber.match(/^(\d{3})(\d{4})(\d{4})$/);
        if (match) {
          return countryCodePrefix ? `${countryCodePrefix} ${match[1]} ${match[2]} ${match[3]}` : `${match[1]} ${match[2]} ${match[3]}`;
        }
      }

      // Return original with prefix if available
      return countryCodePrefix ? `${countryCodePrefix} ${phoneNumber}` : phoneNumber; 
    }

    onPhoneNumberInput(event: any) {
        const formattedNumber = this.formatPhoneNumberLive(event.target.value);
        this.branchDetailsForm.get('branchPhone')?.setValue(formattedNumber, { emitEvent: false });
      }  
  

    onStateChange() {
        this.branchDetailsForm.patchValue({countyId: null, cityId: null});
    }

    resetCountryField() {
        const countryField = this.branchDetailsForm.get('countryId');
        countryField?.enable();
        this.branchDetailsForm.patchValue({countryId: null});
        this.onStateChange() // reset State dropdown
    }

    onCompanyChange(event: Event) {
        const selectedValue = event.target as HTMLSelectElement;
        const countryField = this.branchDetailsForm.get('countryId');
        if(selectedValue.value){
            const getSelectedCompanyType = this.companyList().filter((company) => String(company.companyId) === selectedValue.value);
           if(getSelectedCompanyType.length > 0 && getSelectedCompanyType[0]?.companyType === CompanyType.BROKER_COMPANY){
                // countryField?.disable();
                // countryField?.setValue(CountryType.US);
                // this.branchDetailsForm.patchValue({stateId: null});
                // this.onStateChange() // reset State dropdown
                this.isAdminCompany.emit(false);
            }else{
                // this.resetCountryField();
                this.isAdminCompany.emit(true);
            }
        }else{
            // this.resetCountryField();
        }
    }

    getCountryList() {
        this.systemAdminService.getCountryList().subscribe({
            next: (response: any) => {
                if(response){
                    this.countryList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
            }
        })
    }

    getStatesByCountryId(countryId: number) {
        this.systemAdminService.getStateByCountryId(countryId).subscribe({
            next: (response:any) => {
                if(response && response?.length > 0){
                    this.statesList.set(response);
                    this.filteredStatesList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
            } 
        })
    }

    onCountryChange(event: any){
        this.branchDetailsForm.patchValue({zipCode:null,    stateId:null, countyId: null, cityId: null});
        const selectedCountryId = event.target.value;   
        this.countryId=selectedCountryId
        // this.rawPhoneNumber = null;
        
        // // Update countryCode in form based on selected country
        // if (this.countryList().length > 0) {
        //   const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(selectedCountryId));
        //   const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
        //   this.branchDetailsForm.get('countryCode')?.setValue(countryCode);
        // }
             
        this.countryChange.emit(selectedCountryId);
    }


    getStateName(stateId: string | null): string {
        if (!stateId) return '';
        const state =Array.from(this.statesList().values()).flat().find(s => String(s.stateId) === String(stateId));
        return state ? state.stateName : '';
    }

    getCountyName(countyId: string | null): string {
        if (!countyId) return '';
        const county = this.countyList().find(c => String(c.countyId) === String(countyId));
        return county ? county.countyName : '';
    }

    getCityName(cityId: string | null): string {
        if (!cityId) return '';
        const city = this.citiesList().find(c => String(c.cityId) === String(cityId));
        return city ? city.cityName : '';
    }

    onStateDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchState.set('');
            this.filteredStatesList.set([...this.statesList()]);
        }
    }

    onCountyDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCounty.set('');
            this.filteredCountyList.set([...this.countyList()]);
        }
    }

    onCityDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCity.set('');
            this.filteredCityList.set([...this.citiesList()]);
        }
    }

    onStateSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailState", 1200, () => {
          this.updateStateSearchTerm(value);
        });
    }

    onCountySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCounty", 1200, () => {
          this.updateCountySearchTerm(value);
        });
    }

    onCitySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCity", 1200, () => {
          this.updateCitySearchTerm(value);
        });
    }

    updateStateSearchTerm(value: string) {
        this.searchState.set(value);
        const filtered = this.statesList().filter((state) =>
            state.stateName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredStatesList.set(filtered);
    }

    updateCountySearchTerm(value: string) {
        this.searchCounty.set(value);
        const filtered = this.countyList().filter((county) =>
          county.countyName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCountyList.set(filtered);
    }

    updateCitySearchTerm(value: string) {
        this.searchCity.set(value);
        const filtered = this.citiesList().filter((city) =>
          city.cityName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCityList.set(filtered);
    }

    getStateCountyAndCitiesList(countryId: number, stateId: number) {
        forkJoin([
            this.systemAdminService.getStateByCountryId(countryId),
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId)
        ]).pipe(
            map(([state, counties, cities]) => {
              return {state, counties, cities};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    const countiesSortedList = this.propertyService.sortCountyRecords(response.counties);
                    const citiesSortedList = this.propertyService.sortCityRecords(response.cities);
                    this.statesList.set(response?.state)
                    this.countyList.set(countiesSortedList);
                    this.citiesList.set(citiesSortedList);
                    this.filteredStatesList.set(response?.state);
                    this.filteredCountyList.set(countiesSortedList);
                    this.filteredCityList.set(citiesSortedList);
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    getCompanyList() {
        this.systemAdminService.getAllCompanyList().subscribe({
            next: (response:any) => {
                if(response && response?.length > 0){
                    this.companyList.set(response)
                }
            },
            error: (error: any) => {
                console.log("error", error);
            } 
        })
    }

    allowOnlyAlphanumeric(event: KeyboardEvent): void {
        const key = event.key;
        
        // Check if the key is not an alphanumeric character (0-9, a-z, A-Z)
        if (!/^[a-zA-Z0-9 ]$/.test(key) && key !== "Backspace" && key !== "Delete" && key !== "ArrowLeft" && key !== "ArrowRight") {
            event.preventDefault(); // Prevent non-alphanumeric input
        }
    }

    getCompanyAndStateList() {
        forkJoin([
            this.systemAdminService.getAllCompanyList(),
            this.propertyService.getStates()
        ]).pipe(
            map(([company, states]) => {
                return {company, states};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    this.companyList.set(response.company)
                    this.statesList.set(response.states)
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    getCountyAndCitiesList(stateId: number) {    
        forkJoin([
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId)
        ]).pipe(
            map(([counties, cities]) => {
                return {counties, cities};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    const countiesSortedList = this.propertyService.sortCountyRecords(response.counties);
                    const citiesSortedList = this.propertyService.sortCityRecords(response.cities);
                    this.countyList.set(countiesSortedList);
                    this.citiesList.set(citiesSortedList);
                    this.filteredCountyList.set(countiesSortedList);
                    this.filteredCityList.set(citiesSortedList);
                    //const getCountryFromSelectedState = this.statesList().filter((state) => state.stateId === stateId);
                    // setting the country Id from selected State
                    //getCountryFromSelectedState.length > 0 && this.branchDetailsForm.patchValue({countryId: getCountryFromSelectedState[0].countryId})
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    onCancel(): void{
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
          data: { message: "Unsaved changes will be lost if you navigate from this page. Do you want to continue?" }
        });
    
        dialog.afterClosed().subscribe((result: boolean) => {
          if(result){
            this.branchDetailsForm.reset();
            this.router.navigate(['/system-admin-dashboard/branch']);
          }
        })
    }

    onSubmit(){
        this.branchDetailsSubmitEmitter.emit(true);
    }



     allowStreetNumberCharacters(event: KeyboardEvent): void {
      const allowedPattern = /^[0-9a-zA-Z\-\/ .#]$/;  
      const key = event.key;

      // allow control keys like Backspace, Delete, Tab, Arrow keys
      if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight'
      ) {
        return;
      }

      if (!allowedPattern.test(key)) {
        event.preventDefault();
      }
    }

    
      onZipCodeBlur() {
        
        const zipcode = this.branchDetailsForm.get('zipCode')?.value;
        const countryId = this.branchDetailsForm.get('countryId')?.value;
        
        // Only fetch state by zip code if country is US (1)
        // Exit early if zipcode is missing
        if (!zipcode) {
            return;
        }
        
        // Exit early if countryId is missing, null, undefined, or empty string
        if (countryId === null || countryId === undefined || countryId === '') {
            return;
        }
        
        // Convert to number and validate
        const countryIdNum = Number(countryId);
        if (isNaN(countryIdNum)) {
            return; // Invalid countryId, exit
        }
        
        // Only proceed if countryId equals US (1) - explicitly check for 1
        if (countryIdNum === CountryType.US) {
            this.fetchStateByZipCode(zipcode);
        }
        // If countryId is UK (2) or any other country, do nothing
    }

     fetchStateByZipCode( zipcode: string) {
        const countryId = this.branchDetailsForm.get('countryId')?.value;
        this.propertyService.getZipCode(zipcode, countryId).subscribe({
          next: (response: any) => {
            if(response && response?.State?.length > 0){
              //this.updateStateList(index, response?.State); // update State List
              this.fetchCountyAndCity(response?.State[0], response?.State, response?.County, response?.City);

              this.branchDetailsForm.patchValue({cityId: Number(response?.City[0]), countyId: Number(response?.County[0]), stateId: response?.State[0]});     

           
              
            }else{
              this.toastService.addNotification({message: "Invalid Zip Code, please enter a valid Zip Code", type: ToastType.Error}); // error notification
              this.branchDetailsForm.patchValue({cityId: '', countyId: '', stateId: ''});
              this.statesList.set([]); // reset the list for invalid record
              this.countyList.set([]); // reset the list for invalid record
              this.citiesList.set([]); // reset the list for invalid record
              this.filteredStatesList.set([]); // reset the list for invalid record
              this.filteredCountyList.set([]); // reset the list for invalid record
              this.filteredCityList.set([]); // reset the list for invalid record
            }
          },
          error: (error: any) => {
            this.branchDetailsForm.patchValue({city: '', country: '', state: ''});
            this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
          }
        })
      }

      fetchCountyAndCity(stateId: number, stateList: any[], countiesList: any[] = [], citiesList: any[] = []){
          this.isLoading.set(true);
          forkJoin([
            this.propertyService.getStates(),
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId),
          ]).pipe(
            map(([statesList, countyList, cityList]) => { return {statesList, countyList, cityList}})
          ).subscribe({
            next: (response: any) => {
                if(response){
                  this.isLoading.set(false);
                  this.updateStateList( response.statesList, stateList);
                  
                  const countiesSortedList = this.propertyService.sortCountyRecords(response.countyList);
                  const citiesSortedList = this.propertyService.sortCityRecords(response.cityList);
                  if(countiesList?.length > 0 && citiesList?.length > 0){
                    const filteredCounties = countiesSortedList.filter(countyId => countiesList.includes(countyId.countyId));
                    const filteredCities = citiesSortedList.filter(cityId => citiesList.includes(cityId.cityId));
                    this.updateCountyAndCityList(filteredCounties, filteredCities);
                  }else{
                    this.updateCountyAndCityList(countiesSortedList, citiesSortedList);
                  }
                }
            },
            error: (error: any) => {
                this.isLoading.set(false);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
          })
        }

     updateStateList(stateList: any[], stateIDList: any[]) {
            const filteredStates = stateList.filter(state => stateIDList.includes(state.stateId));
            this.statesList.set(filteredStates);
            this.filteredStatesList.set(filteredStates);
        }

        updateCountyAndCityList(countiesList: any[] = [], citiesList: any[] = []) {
            this.countyList.set(countiesList);
            this.citiesList.set(citiesList);
            this.filteredCountyList.set(countiesList);            
            this.filteredCityList.set(citiesList);
            // this.isCityStateListFetched.set(true);
        }
      

    ngOnDestroy(): void {
        // Unsubscribe to prevent memory leaks
        this.companySubscription?.unsubscribe();
        this.stateSubscription?.unsubscribe();
        this.countySubscription?.unsubscribe();
        this.citySubscription?.unsubscribe();
        this.countrySubscription.unsubscribe();
        this.phoneInputSubscription.unsubscribe();
    }
}   